import { useEffect, useRef, useState } from "react";
import operatorApiService from "../../../services/operator-api.service"; // ← APIサービスは適宜調整
import "./GuideCanvas.scss";

type LineShape = {
  type: "line";
  color: string;
  width: string;
  points: [number, number, number, number];
};

type TriangleShape = {
  type: "triangle";
  color: string;
  points: [number, number, number, number, number, number];
  flatwidth?: string;
};

type TrapezoidShape = {
  type: "trapezoid";
  color: string;
  points: [number, number, number, number, number, number, number, number];
};

type GuideLine = LineShape | TriangleShape | TrapezoidShape;

type Props = {
  selectingVehicleId?: number;
  guide: number;
  videoPat: number;
};

const GuideCanvas = ({ selectingVehicleId, guide, videoPat }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [guideLines, setGuideLines] = useState<GuideLine[]>([]);

  useEffect(() => {
    const fetchGuideLines = async () => {
      if (!guide || !videoPat || !selectingVehicleId) {
        setGuideLines([]);
        return;
      }

      try {
        const response = await operatorApiService.doGetGuideLines({
          selectingVehicleId,
          guide,
          videoPat,
        });
        const shapeJson = response[0]?.shape_data;
        const parsed = shapeJson ? JSON.parse(shapeJson) : [];
        console.error("ガイド線データ:", parsed);
        setGuideLines(parsed);
      } catch (err) {
        console.error("ガイド線データ取得エラー:", err);
      }
    };

    fetchGuideLines();
  }, [guide, videoPat]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;

    const draw = () => {
      const width = parent.offsetWidth;
      const height = parent.offsetHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      const scaleX = width / 1920;
      const scaleY = height / 1080;

      const sx = (x: number) => x * scaleX;
      const sy = (y: number) => y * scaleY;

      for (const item of guideLines) {
        if (item.type === "line" && Array.isArray(item.points)) {
          const [x1, y1, x2, y2] = item.points;
          ctx.beginPath();
          ctx.lineWidth = parseFloat(item.width ?? "4");
          ctx.strokeStyle = item.color;
          ctx.moveTo(sx(x1), sy(y1));
          ctx.lineTo(sx(x2), sy(y2));
          ctx.stroke();
        }

        if (item.type === "triangle" && Array.isArray(item.points)) {
          const [x1, y1, x2, y2, x3, y3] = item.points;
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;
          const angle = Math.atan2(y3 - midY, x3 - midX);
          const flatWidth = Number(item.flatwidth)
          const x3Left = x3 - Math.cos(angle) * (flatWidth / 2);
          const x3Right = x3 + Math.cos(angle) * (flatWidth / 2);
          const y3Flat = y3 - Math.sin(angle) * (flatWidth / 2);

          ctx.beginPath();
          ctx.moveTo(sx(x1), sy(y1));
          ctx.lineTo(sx(x2), sy(y2));
          ctx.lineTo(sx(x3Right), sy(y3Flat));
          ctx.lineTo(sx(x3Left), sy(y3Flat));
          ctx.closePath();
          ctx.fillStyle = item.color;
          ctx.fill();
        }

        if (item.type === "trapezoid" && Array.isArray(item.points)) {
          const [x1, y1, x2, y2, x3, y3, x4, y4] = item.points;
          ctx.beginPath();
          ctx.moveTo(sx(x1), sy(y1));
          ctx.lineTo(sx(x2), sy(y2));
          ctx.lineTo(sx(x3), sy(y3));
          ctx.lineTo(sx(x4), sy(y4));
          ctx.closePath();
          ctx.fillStyle = item.color;
          ctx.fill();
        }
      }
    };

    const observer = new ResizeObserver(draw);
    observer.observe(parent);

    draw();

    return () => observer.disconnect();
  }, [guideLines]);

  return <canvas ref={canvasRef} className="guideCanvas" />;
};

export default GuideCanvas;
